import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Badge from 'LuxKit/Badge'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CarsAirportTransferSearchBar from './AirportTransfer/CarsAirportTransferSearchBar'
import CarsCarHireSearchBar from './CarHire/CarsCarHireSearchBar'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CarsAirportTransferMobileSearchBar from './AirportTransfer/CarsAirportTransferMobileSearchBar'
import {
  CARS_AIRPORT_TRANSFER_SEARCH_ID,
  type AirportTransferSearchEmptyField,
  type CarsAirportTransferSearchFieldHandle,
  type CarsAirportTransferSearchPromptHandle,
} from './hooks/useAirportTransferSearchPrompt'

export type CarsTab = 'car-hire' | 'airport-transfer'

const Root = styled.div`
  position: relative;
`

const HeroImage = styled(ResponsiveImage)`
  position: absolute;
  max-height: calc(100% - ${rem(48)});
  top: 0;
  inset-inline-start: 0;
  width: 100%;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(280)};
    max-height: ${rem(280)};
  }
`

const HeroContainer = styled.div`
  position: relative;
  padding: ${rem(30)} 0;

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(80)};
    padding-bottom: ${rem(32)};
  }
`

const Background = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const SearchInputsContainer = styled(Sheet)`
  width: 100%;

  ${mediaQueryUp.tablet} {
    margin-top: ${rem(46)};
  }
`

const SearchAnchor = styled.div`
  width: 100%;
`

const FullWidthBreakpoint = styled(CSSBreakpoint)`
  width: 100%;
`

interface Props {
  activeTab: CarsTab
  isCarHireSearchEnabled: boolean
  onTabChange: (tab: CarsTab) => void
}

const CarsPageHeader = forwardRef<CarsAirportTransferSearchPromptHandle, Props>(function CarsPageHeader(props, ref) {
  const { activeTab, isCarHireSearchEnabled, onTabChange } = props
  const intl = useIntl()
  const searchAnchorRef = useRef<HTMLDivElement>(null)
  const searchFieldsRef = useRef<CarsAirportTransferSearchFieldHandle>(null)

  const selectCarHire = useCallback(() => {
    onTabChange('car-hire')
  }, [onTabChange])

  const selectAirportTransfer = useCallback(() => {
    onTabChange('airport-transfer')
  }, [onTabChange])

  const scrollIntoView = useCallback(() => {
    searchAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const openFirstEmptyField = useCallback((field: AirportTransferSearchEmptyField | undefined) => {
    searchFieldsRef.current?.openFirstEmptyField(field)
  }, [])

  useImperativeHandle(ref, () => ({
    scrollIntoView,
    openFirstEmptyField,
  }), [openFirstEmptyField, scrollIntoView])

  const title = intl.formatMessage({
    id: 'xDoZIGFxMm8U9i7wD551',
    defaultMessage: 'Get there in comfort',
    description: 'Main hero heading displayed at the top of the Cars booking page',
  })

  const description = intl.formatMessage({
    id: 'Unf1KQqX7Hi_F0bza9GG',
    defaultMessage: 'Hire a car or book a private airport transfer worldwide.',
    description: 'Supporting hero description for the Cars booking page search widget',
  })

  return (
    <Root>
      <Background />
      <HeroImage id="cars-page-hero" fit="center" objectFit="cover" height={280} quality="best" />
      <HeroContainer>
        <LayoutContainer variant="large" className="hero-heading">
          <Group direction="vertical" gap={8} tabletHorizontalAlign="center" fullWidth>
            <CSSBreakpoint min="tablet"><Heading variant="heading1" colour="neutral-eight" align="center">{title}</Heading></CSSBreakpoint>
            <CSSBreakpoint min="tablet"><BodyText variant="large" colour="neutral-eight" align="center">{description}</BodyText></CSSBreakpoint>

            <CSSBreakpoint max="mobile"><Heading variant="heading1" colour="neutral-eight" align="start">{title}</Heading></CSSBreakpoint>
            <CSSBreakpoint max="mobile"><BodyText variant="small" colour="neutral-eight" align="start">{description}</BodyText></CSSBreakpoint>

            <FullWidthBreakpoint min="tablet">
              <SearchAnchor id={CARS_AIRPORT_TRANSFER_SEARCH_ID} ref={searchAnchorRef}>
                <SearchInputsContainer background="neutral-eight" borderRadius="M" shadow="bottom-medium" padding="0 16 16 16">
                  <Group direction="vertical" gap={16}>
                    <TabButtonsGroup>
                      <TabButton
                        size="medium"
                        isActive={activeTab === 'airport-transfer'}
                        onClick={selectAirportTransfer}
                        endIcon={
                          <Badge kind="string" mode="critical">
                            {intl.formatMessage({
                              id: '3T_1XH6k_AgHhNM32Ham',
                              defaultMessage: 'New',
                              description: 'Badge identifying the newly available airport transfer search feature',
                            })}
                          </Badge>
                        }
                      >
                        {intl.formatMessage({
                          id: 'pA9ekRVx5pqfz_vtxarw',
                          defaultMessage: 'Airport transfer',
                          description: 'Airport transfer tab label shown in the Cars search widget navigation',
                        })}
                      </TabButton>
                      {isCarHireSearchEnabled && <TabButton
                        size="medium"
                        isActive={activeTab === 'car-hire'}
                        onClick={selectCarHire}
                      >
                        {intl.formatMessage({
                          id: 'R2Fc76MmY3ZDALupQFYF',
                          defaultMessage: 'Car hire',
                          description: 'Car hire tab label shown in the Cars search widget navigation',
                        })}
                      </TabButton>}
                    </TabButtonsGroup>
                    {activeTab === 'car-hire' && isCarHireSearchEnabled && <CarsCarHireSearchBar />}
                    {activeTab === 'airport-transfer' && (
                      <CarsAirportTransferSearchBar ref={searchFieldsRef} />
                    )}
                  </Group>
                </SearchInputsContainer>
              </SearchAnchor>
            </FullWidthBreakpoint>
            <FullWidthBreakpoint max="mobile">
              <CarsAirportTransferMobileSearchBar />
            </FullWidthBreakpoint>
          </Group>
        </LayoutContainer>
      </HeroContainer>
    </Root>
  )
})

export default CarsPageHeader
